import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { FormikProps } from "formik/dist/types";
import { useRouter } from "next/router";
import { ChangeEvent, useEffect, useState } from "react";
import { getActivePlan } from "src/slices/auth/thunks";
import { languages, locations } from "src/slices/brief/helpers";
import { getProjects, projects } from "src/slices/projects";
import { useDispatch, useSelector } from "src/store";
type Props = {
  formik: FormikProps<{
    keyword: string;
    location: (typeof locations)[0];
    language: string;
    project: {
      projectName: string;
      projectId: string;
      projectDomain: string;
      isGSCIntegrated: boolean;
      companyDetails: any;
      GSCsiteUrl: string;
      excludedLinks: string[];
    };
    articleLink: string;
    stats: boolean;
    insights: string;
    interlinking: boolean;
    aiEvalRefineAgent: boolean;
  }>;
  isShowInput?: boolean;
};

const ChooseKeyword = ({ formik, isShowInput }: Props) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const filter = createFilterOptions();
  const Project = formik.values.project;

  let Projects = useSelector(projects);
  const updatedProjects = [
    ...Projects,
    {
      projectId: "new",
      projectName: "Add new project",
      projectDomain: "",
      isGSCIntegrated: false,
      GSCsiteUrl: "",
      createdAt: "",
      updatedAt: "",
      briefCount: 0,
      briefs: [],
      AImodel: "claude",
      excludedLinks: [],
      brandVoice: "",
      companyDetails: {
        companyName: "",
        companyInformation: "",
        productInformation: "",
        targetAudience: "",
      },
      isSeoMissionCreated: false,
      competitors: [],
      optimizedArticleCount: 0,
    },
  ];
  const [currentPlan, setCurrentPlan] = useState<any>({});
  const [isInterLinkingAdded, setIsInterLinkingAdded] = useState(false);
  // const currentPlan: any = useSelector(selectCurrentPlan);
  const getCurrentPlan = async () => {
    const currentPlan = (await dispatch(getActivePlan())) as any;
    setCurrentPlan(currentPlan?.payload?.plan);
  };
  useEffect(() => {
    getCurrentPlan();
  }, []);

  const [insights, setInsights] = useState(false);
  const [statsToggletext, setStatsToggletext] = useState(
    "We'll research and add relevant stats and quotes to your AI article if they match your outline."
  );
  const [interLinkingToggletext, setInterLinkingToggletext] = useState("");
  const [placeholderText, setPlaceholderText] = useState(
    "Add your insights here..."
  );
  const [aiEvalRefineAgentToggletext, setAiEvalRefineAgentToggletext] =
    useState(
      "Enable AI-powered evaluation to improve your article based on analysis, data enrichment, and refinement. This feature rewrites and fine-tunes your content for better clarity, authority, and SEO minimizing editing on your end."
    );
  const isStatsAvailableAtPlan =
    currentPlan?.stats === "true" || currentPlan?.stats === true;
  const isInterLinkingAvailableAtPlan =
    currentPlan?.interlinking === "true" || currentPlan?.interlinking === true;
  const isInsightsAvailableAtPlan =
    currentPlan?.insights === "true" || currentPlan?.insights === true;
  const handleInsightsAdded = (event: ChangeEvent<any>) => {
    setInsights(event.target.checked);
    formik.setFieldValue("brief.insights", "");
  };

  const isAiEvalRefineAgentAvailableAtPlan =
    currentPlan?.aiEvalAgent === "true" || currentPlan?.aiEvalAgent === true;

  useEffect(() => {
    if (formik.values?.insights) {
      setInsights(true);
      formik.setFieldValue("insights", formik.values.insights);
    }
  }, [formik.values.insights]);

  useEffect(() => {
    if (insights) {
      setStatsToggletext(
        "We'll research and add relevant stats and quotes to your AI article if they match your outline."
      );
      setPlaceholderText(
        "Add your personal experience, know-how, data and quotes to enrich the AI article. Add each insight in a separate row for better clarity."
      );
    } else {
      setStatsToggletext(
        "We'll research and add relevant stats and quotes to your AI article if they match your outline."
      );
      setPlaceholderText(
        "SEOwind will read and analyze content you paste here to learn how to communicate in your voice."
      );
    }
  }, [insights]);

  useEffect(() => {
    if (isAiEvalRefineAgentAvailableAtPlan) {
      formik.setFieldValue("aiEvalRefineAgent", true);
      setAiEvalRefineAgentToggletext(
        "Enable AI-powered evaluation to improve your article based on analysis, data enrichment, and refinement. This feature rewrites and fine-tunes your content for better clarity, authority, and SEO minimizing editing on your end."
      );
    } else {
      setAiEvalRefineAgentToggletext(
        "Upgrade to a higher plan and enable AI-powered evaluation to improve your article based on analysis, data enrichment, and refinement. This feature rewrites and fine-tunes your content for better clarity, authority, and SEO minimizing editing on your end."
      );
      formik.setFieldValue("aiEvalRefineAgent", false);
    }
  }, [currentPlan]);

  const handleLocationChange = (event: ChangeEvent<any>, value) => {
    formik.setFieldValue("location", value);
    localStorage.setItem("brief-location", JSON.stringify(value));
  };

  const handleLanguageChange = (event: ChangeEvent<any>) => {
    formik.handleChange(event);
    localStorage.setItem("brief-language", event.target.value);
  };

  /* projects commented out */
  const handleProjectChange = (event: ChangeEvent<any>, value) => {
    if (value?.projectId === "new") {
      handleNewProject(event as any);
      return;
    }
    formik.setFieldValue("project", value);
    localStorage.setItem("brief-project", JSON.stringify(value));
  };
  const handleNewProject = (event) => {
    event.stopPropagation();
    router.push({
      pathname: "/projects/settings/",
      query: {
        url: "new",
      },
    });
  };

  const redirectToProject = () => {
    if (Project?.projectId) {
      return `/app/projects/settings?url=edit&id=${Project?.projectId}`;
    }
    return "/app/projects";
  };

  useEffect(() => {
    if (Project?.isGSCIntegrated && isInterLinkingAvailableAtPlan) {
      setIsInterLinkingAdded(true);
      formik.setFieldValue("interlinking", true);

      setInterLinkingToggletext(
        "We'll add relevant internal links to your article by analyzing your article, data from Google and GSC. To integrate with your GSC go to <a style='color: #eef3fd; ' href=" +
          redirectToProject() +
          "> " +
          `${Project?.projectName}` +
          "</a> Project Settings."
      );
    } else if (!Project?.isGSCIntegrated && isInterLinkingAvailableAtPlan) {
      setIsInterLinkingAdded(false);
      formik.setFieldValue("interlinking", false);

      setInterLinkingToggletext(
        "We'll add relevant internal links to your article by analyzing your article, data from Google and GSC. To integrate with your GSC go to <a style='color: #eef3fd; ' href=" +
          redirectToProject() +
          "> " +
          `${Project?.projectName}` +
          "</a> Project Settings."
      );
    } else {
      setIsInterLinkingAdded(false);
      formik.setFieldValue("interlinking", false);

      setInterLinkingToggletext(
        "We'll add relevant internal links to your article by analyzing your article, data from Google and GSC. Upgrade your plan to unlock the feature."
      );
    }
  }, [Project, isInterLinkingAvailableAtPlan]);

  useEffect(() => {
    dispatch(getProjects());
  }, []);

  // console.log("Projects in keywords", Projects);

  return (
    <Box sx={{ p: 3, pt: 4 }}>
      <Typography align="center" gutterBottom variant="h5">
        Update Your Content
      </Typography>
      <Typography align="center" gutterBottom variant="body1">
        Our AI workflow with AI agents will audit, research, rewrite, optimize
        for E-E-A-T, and add internal links.
      </Typography>
      <Box sx={{ pt: 3 }}>
        <TextField
          fullWidth
          label="Focus keyword"
          name="keyword"
          type="text"
          value={formik.values.keyword}
          defaultValue={formik.values.keyword}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          disabled={isShowInput}
          helperText={
            formik.errors.keyword && formik.touched
              ? formik.errors.keyword
              : undefined
          }
          sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
        />
      </Box>
      <Box sx={{ pt: 3 }}>
        <TextField
          fullWidth
          label="Article Link"
          name="articleLink"
          type="text"
          value={formik.values.articleLink}
          defaultValue={formik.values.articleLink}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          disabled={isShowInput}
          helperText={
            formik.errors.articleLink && formik.touched
              ? formik.errors.articleLink
              : undefined
          }
          sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
        />
      </Box>
      <Grid container gap={1} sx={{ pt: 3 }}>
        <Grid>
          <FormControl fullWidth>
            <Autocomplete
              id="location"
              options={locations}
              disabled={isShowInput}
              getOptionLabel={(option: {
                location_name: string;
                country_iso_code: string;
              }) => option.location_name}
              sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Location"
                  helperText={
                    formik.errors.location && formik.touched
                      ? formik.errors.location
                      : undefined
                  }
                  sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
                />
              )}
              value={formik.values.location}
              onChange={handleLocationChange}
              renderOption={(props, option) => (
                <MenuItem
                  key={option.location_name}
                  value={option.location_name}
                  {...props}
                >
                  {option.location_name}
                </MenuItem>
              )}
            />
          </FormControl>
        </Grid>
        <Grid flex={1}>
          <FormControl fullWidth>
            <InputLabel id="language-label">Language</InputLabel>
            <Select
              labelId="language-label"
              id="language"
              value={formik.values.language}
              label="language"
              name="language"
              onChange={handleLanguageChange}
              defaultValue="English"
              disabled={isShowInput}
            >
              {languages
                .sort((a, b) => {
                  if (a.languageName > b.languageName) {
                    return 1;
                  } else if (a.languageName < b.languageName) {
                    return -1;
                  } else {
                    return 0;
                  }
                })
                .map((value) => (
                  <MenuItem key={value.languageName} value={value.languageName}>
                    {value.languageName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      {/* projects commented out */}
      <Box sx={{ pt: 3 }}>
        <FormControl fullWidth>
          <Autocomplete
            id="project"
            options={updatedProjects}
            getOptionLabel={(option: {
              projectName: string;
              projectDomain: string;
              projectId: string;
            }) =>
              // Value selected with enter, right from the input
              option.projectName
            }
            sx={{ width: 552 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Choose a project"
                helperText={
                  formik.errors.project && formik.touched.project
                    ? formik.errors.project
                    : undefined
                }
                sx={{ "& .MuiFormHelperText-root": { color: "#D14343" } }}
              />
            )}
            value={formik.values.project}
            onChange={handleProjectChange}
            filterOptions={(options, params) => {
              let filtered = filter(options, params);
              if (params.inputValue !== "") {
                //show projecy with projectId new
                filtered.push(updatedProjects[updatedProjects.length - 1]);
              }
              return filtered;
            }}
            selectOnFocus
            freeSolo
            renderOption={(props, option) => (
              <>
                {option.projectId === "new" ? (
                  <MenuItem
                    key={option.projectId}
                    value={option.projectId}
                    {...props}
                  >
                    <Box
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      sx={{
                        width: "100%",
                        // paddingLeft: "1rem",
                      }}
                    >
                      <Button
                        variant="outlined"
                        sx={{
                          fontSize: "11px",
                          fontWeight: "600",
                          fontFamily: "Inter",
                          color: "#5048E5",
                          float: "left",
                          marginTop: "0.5rem",
                          // marginBottom: "1.5rem",
                          borderRadius: "4px",
                          padding: "8px",
                        }}
                        onClick={(event) => {
                          handleNewProject(event);
                        }}
                      >
                        <span style={{ marginRight: "0.5rem" }}>+</span>
                        Add New Project
                      </Button>
                    </Box>
                  </MenuItem>
                ) : (
                  <MenuItem
                    key={option.projectId}
                    value={option.projectId}
                    {...props}
                  >
                    {option.projectName}
                  </MenuItem>
                )}
              </>
            )}
          />
        </FormControl>
      </Box>
      <Box sx={{ pt: 3 }}>
        <Typography
          gutterBottom
          sx={{ fontWeight: 500, fontSize: "1.1rem", mr: 1 }}
        >
          Update the article with:
        </Typography>
        {/* interlinking toggle */}
        <Box display={"flex"} alignItems={"center"}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  onChange={formik.handleChange}
                  // disabled={!isInterLinkingAdded}
                  checked={formik.values.interlinking}
                  // defaultChecked={interLinkingChecked}
                  name="interlinking"
                  value={formik.values.interlinking}
                  disabled={isShowInput || !isInterLinkingAdded}
                />
              }
              label="Internal Linking"
            />
          </FormGroup>
          {/* add tool tip  */}
          <Tooltip
            title={
              <div
                dangerouslySetInnerHTML={{
                  __html:
                    interLinkingToggletext ||
                    "We'll add internal links to your AI article if they match your outline.",
                }}
              />
            }
            placement="top"
          >
            <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
          </Tooltip>
        </Box>

        {/* AI Eval & Refine Agent  */}
        <Box display={"flex"} alignItems={"center"}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  onChange={formik.handleChange}
                  disabled={!isAiEvalRefineAgentAvailableAtPlan}
                  checked={formik.values?.aiEvalRefineAgent}
                  // defaultChecked={interLinkingChecked}
                  name="aiEvalRefineAgent"
                  value={formik.values?.aiEvalRefineAgent}
                />
              }
              label="AI Eval & Refine Agent"
            />
          </FormGroup>
          {/* add tool tip  */}
          <Tooltip
            title={
              <div
                dangerouslySetInnerHTML={{
                  __html: aiEvalRefineAgentToggletext,
                }}
              />
            }
            placement="top"
          >
            <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
          </Tooltip>
        </Box>
        {/* a sublist of tasks for ai eval & refine agent with icons check */}
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{
            ml: "0.5rem",
            mt: "-0.5rem",
          }}
        >
          <List sx={{ px: 0.3 }}>
            <ListItem
              sx={{
                py: 0.3,
                "& .MuiListItemText-root": {
                  my: 0,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {formik.values?.aiEvalRefineAgent ? (
                  <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Reviews initial article and provides feedback"
                primaryTypographyProps={{ fontSize: "0.875rem" }}
                sx={{
                  ml: "-0.5rem",
                }}
              />
            </ListItem>
            <ListItem
              sx={{
                py: 0.3,
                "& .MuiListItemText-root": {
                  my: 0,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {formik.values?.aiEvalRefineAgent ? (
                  <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Gathers additional data and insights"
                primaryTypographyProps={{ fontSize: "0.875rem" }}
                sx={{
                  ml: "-0.5rem",
                }}
              />
            </ListItem>
            <ListItem
              sx={{
                py: 0.3,
                "& .MuiListItemText-root": {
                  my: 0,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {formik.values?.aiEvalRefineAgent ? (
                  <CheckIcon fontSize="small" sx={{ color: "#9BCD3D" }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: "#DC3D3D" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary="Rewrites & refines the final article for E-E-A-T"
                primaryTypographyProps={{ fontSize: "0.875rem" }}
                sx={{
                  ml: "-0.5rem",
                }}
              />
            </ListItem>
          </List>
        </Box>
        {/* insights toggle */}
        <Box display={"flex"} alignItems={"center"}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  onChange={handleInsightsAdded}
                  // disabled={!isInsightsAdded}
                  checked={insights}
                  defaultChecked={insights}
                  disabled={isShowInput}
                />
              }
              label="Your own insights"
            />
          </FormGroup>
          {/* add tool tip  */}
          {/* <Tooltip
                title={
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        interLinkingToggletext ||
                        "We'll add internal links to your AI article if they match your outline.",
                    }}
                  />
                }
                placement="top"
              >
                <InfoIcon sx={{ fontSize: "16px", ml: -1.5 }} />
              </Tooltip> */}
        </Box>

        {insights && (
          <Box
            // display={"flex"}
            alignItems={"center"}
            width="100%"
            // height="170px"
          >
            <Box width="100%" height="53px" top="4px" gap="4px">
              <Typography
                color="#121828"
                sx={{
                  fontWeight: "500",
                  fontFamily: "Inter",
                  fontSize: "16px",
                }}
              >
                Add your insights
              </Typography>
              <Typography
                color="#7B85AD"
                sx={{
                  fontWeight: "400",
                  fontFamily: "Inter",
                  fontSize: "12px",
                }}
              >
                Add your personal experience, know-how, data and quotes to
                enrich the AI article. Add each insight in a separate row for
                better clarity.
              </Typography>
            </Box>
            <Box width="100%" marginTop={"10px"}>
              <TextField
                fullWidth
                name="insights"
                placeholder={placeholderText}
                error={
                  formik.touched.insights && Boolean(formik.errors.insights)
                }
                helperText={formik.touched.insights && formik.errors.insights}
                value={formik.values.insights}
                multiline
                rows={10}
                onChange={formik.handleChange}
                disabled={isShowInput}
                // placeholder="SEOwind will read and analyze content you paste here to learn how to communicate in your voice."
                //overriding the defaultValue text style
                sx={{
                  // "& .MuiOutlinedInput-root": {
                  //   "& fieldset": {
                  //     borderColor: "#E6E8F0",
                  //   },
                  //   "&:hover fieldset": {
                  //     borderColor: "#E6E8F0",
                  //   },
                  //   "&.Mui-focused fieldset": {
                  //     borderColor: "#E6E8F0",
                  //   },
                  //   //change label color on focus
                  // },
                  // "& .css-1igsam7-MuiFormLabel-root-MuiInputLabel-root": {
                  //   color: "#000000",
                  //   fontFamily: "Inter",
                  //   fontSize: "12px",
                  //   fontWeight: "400",
                  //   lineHeight: "20px",
                  //   letterSpacing: "0em",
                  //   textAlign: "left",
                  // },
                  //change color of placeholder text
                  "& .css-1wa3eu0-MuiInputBase-input-MuiOutlinedInput-input": {
                    color: "#000000",
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: "400",
                    lineHeight: "20px",
                    letterSpacing: "0em",
                    textAlign: "left",
                  },
                  borderRadius: "8px",
                  border: "1px solid #E6E8F0",
                  // padding: "0px 12px 0px 12px",
                }}
              />
            </Box>
            {/* <Box
            width="100%"
            display={"flex"}
            sx={{
              ml: 1,
              mt: 1,
            }}
          >
            <Box
              display="flex"
              justifyContent="flex-start"
              width={"100%"}
              sx={{
                cursor: "pointer",
              }}
              onClick={() => setIsInsightsDialogOpen(true)}
            > */}
            {/* <Box
                sx={{
                  mt: 0.5,
                  height: "12px",
                  width: "12px",
                  // cursor: "pointer",
                }}
              >
                <ExpandModal
                  sx={
                    {
                      // height: "24px",
                      // width: "24px",
                    }
                  }
                />
              </Box>
              <Box
                sx={{
                  ml: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: "600",
                    fontFamily: "Inter",
                    color: "#5048E5",
                    // fontStyle: "normal",
                    // lineHeight: "normal",
                    textAlign: "right",
                  }}
                >
                  Expand field
                </Typography>
              </Box> */}
            {/* </Box> */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
                mt: 0.3,
                mr: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: "400",
                  fontFamily: "Inter",
                  color: "#7B85AD",
                  fontStyle: "normal",
                  lineHeight: "normal",
                  textAlign: "right",
                }}
              >
                {formik?.values?.insights?.length
                  ? formik?.values?.insights?.length
                  : 0}
                /5000
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box sx={{ pt: 3, textAlign: "right" }}>
        <Button variant="contained" type="submit" disabled={isShowInput}>
          Update Content
        </Button>
      </Box>
    </Box>
  );
};

export default ChooseKeyword;
